import { useCallback, useEffect } from 'react'
import { useUIStore, ACCENT_THEMES, type AccentTheme } from '@/lib/store/ui-store'

interface SettingsDialogProps {
  onClose: () => void
}

interface ThemeViz { label: string; accentLight: string; accentDark: string; railFrom: string; railTo: string; sideText?: string }
const THEMES: Record<AccentTheme, ThemeViz> = {
  cyan: { label: 'Concierge', accentLight: '#8A6E36', accentDark: '#C2A468', railFrom: '#1a1710', railTo: '#0b0a07' },
  violet: { label: 'Violet', accentLight: '#7C3AED', accentDark: '#A78BFA', railFrom: '#211a38', railTo: '#140e26' },
  emerald: { label: 'Emerald', accentLight: '#059669', accentDark: '#34D399', railFrom: '#06241c', railTo: '#04150f' },
  rose: { label: 'Rose', accentLight: '#E11D48', accentDark: '#FB7185', railFrom: '#2b1018', railTo: '#1b0a10' },
  amber: { label: 'Amber', accentLight: '#D97706', accentDark: '#FBBF24', railFrom: '#2a1d08', railTo: '#1a1205' },
  blue: { label: 'Blue', accentLight: '#2563EB', accentDark: '#60A5FA', railFrom: '#0e1f3a', railTo: '#081427' },
  indigo: { label: 'Indigo', accentLight: '#4F46E5', accentDark: '#818CF8', railFrom: '#3a3470', railTo: '#272255' },
  teal: { label: 'Teal', accentLight: '#0D9488', accentDark: '#2DD4BF', railFrom: '#0c4a47', railTo: '#073634' },
  light: { label: 'Daylight', accentLight: '#2563EB', accentDark: '#60A5FA', railFrom: '#eef1f6', railTo: '#e1e6ee', sideText: 'rgb(15 23 42 / 0.35)' },
}

// A tiny mock of the app (rail + chat area + accent bits) drawn with the theme's
// own colors, so each option previews how it affects the whole layout.
function ThemePreview({ viz, accent }: { viz: ThemeViz; accent: string }) {
  return (
    <div className="flex h-14 rounded-md overflow-hidden border border-[rgb(var(--np-border))]">
      <div className="w-[34%] flex flex-col gap-1 p-1.5" style={{ background: `linear-gradient(180deg, ${viz.railFrom}, ${viz.railTo})` }}>
        <span className="h-1.5 rounded-full" style={{ background: accent, width: '80%' }} />
        <span className="h-1.5 rounded-full" style={{ background: viz.sideText ?? 'rgb(255 255 255 / 0.25)', width: '65%' }} />
        <span className="h-1.5 rounded-full" style={{ background: viz.sideText ?? 'rgb(255 255 255 / 0.25)', width: '72%' }} />
      </div>
      <div className="flex-1 flex flex-col gap-1 p-1.5" style={{ backgroundColor: 'rgb(var(--np-surface))' }}>
        <span className="h-1.5 rounded-full" style={{ background: accent, width: '40%' }} />
        <span className="h-1.5 rounded-full bg-[rgb(var(--np-tx-muted)/0.4)]" style={{ width: '85%' }} />
        <span className="h-1.5 rounded-full bg-[rgb(var(--np-tx-muted)/0.4)]" style={{ width: '60%' }} />
      </div>
    </div>
  )
}

export function SettingsDialog({ onClose }: SettingsDialogProps) {
  const theme = useUIStore((s) => s.theme)
  const toggleTheme = useUIStore((s) => s.toggleTheme)
  const accentTheme = useUIStore((s) => s.accentTheme)
  const setAccentTheme = useUIStore((s) => s.setAccentTheme)

  const handleKey = useCallback((e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }, [onClose])
  useEffect(() => {
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [handleKey])

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div
        className="absolute top-14 right-3 w-80 max-w-[calc(100vw-1.5rem)] bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg shadow-xl overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(var(--np-border))]">
          <h3 className="text-sm font-semibold text-[rgb(var(--np-tx-primary))]">Settings</h3>
          <button onClick={onClose} className="icon-btn-sm" title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className="px-4 py-4 space-y-5">
          {/* Appearance mode */}
          <div>
            <p className="label-section mb-2">Appearance</p>
            <div className="flex gap-1 p-1 bg-[rgb(var(--np-raised))] rounded-lg">
              {(['light', 'dark'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => { if (theme !== m) toggleTheme() }}
                  className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-md transition-colors capitalize ${
                    theme === m ? 'bg-[rgb(var(--np-surface))] text-[rgb(var(--np-accent))] shadow-sm' : 'text-[rgb(var(--np-tx-muted))] hover:text-[rgb(var(--np-tx-secondary))]'
                  }`}
                >
                  {m === 'light' ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.2" y1="4.2" x2="5.6" y2="5.6" /><line x1="18.4" y1="18.4" x2="19.8" y2="19.8" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.2" y1="19.8" x2="5.6" y2="18.4" /><line x1="18.4" y1="5.6" x2="19.8" y2="4.2" /></svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
                  )}
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Accent theme — live layout previews */}
          <div>
            <p className="label-section mb-2">Theme</p>
            <div className="grid grid-cols-2 gap-2.5">
              {ACCENT_THEMES.map((t) => {
                const viz = THEMES[t]
                const active = accentTheme === t
                const accent = theme === 'dark' ? viz.accentDark : viz.accentLight
                return (
                  <button
                    key={t}
                    onClick={() => setAccentTheme(t)}
                    className={`p-1.5 rounded-lg border text-left transition-colors ${
                      active ? 'border-[rgb(var(--np-accent))] bg-[rgb(var(--np-accent)/0.06)]' : 'border-[rgb(var(--np-border))] hover:bg-[rgb(var(--np-raised))]'
                    }`}
                  >
                    <ThemePreview viz={viz} accent={accent} />
                    <div className="flex items-center justify-between mt-1.5 px-0.5">
                      <span className={`text-xs ${active ? 'text-[rgb(var(--np-tx-primary))] font-medium' : 'text-[rgb(var(--np-tx-secondary))]'}`}>{viz.label}</span>
                      {active && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgb(var(--np-accent))" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
